import { DISPLAY_IMAGE_CDN, type DisplayImage } from './displayImageContract';

/** Shared CDN sizing presets for normalized DisplayImage URLs. */
export const DISPLAY_IMAGE_WIDTHS = {
  micro: 48,
  compact: 96,
  thumbnail: 160,
  card: 400,
  detail: 1200,
  viewer: 1500,
} as const;

/**
 * Replaces any existing Amazon image-sizing directive with `._SL{width}_`.
 *
 * # Why this is generic rather than regex-matching known directive patterns
 *
 * Amazon uses dozens of directives (`._SL1500_`, `._AC_UY218_`, `._SX355_`,
 * `._SY879_`, `._CR0,0,1500,1500_`, …) and adds new ones regularly. A
 * hardcoded pattern that only strips `._SL*` or `._AC_UY*` will silently
 * leave other directives in place. The frontend then stacks its own directive
 * on top, creating a compound URL like `._AC_UY218_._SL1500_.jpg` that
 * Amazon's CDN resolves to the *smallest* embedded size — giving a 218 px
 * image in a 1500 px viewer.
 *
 * # How it works
 *
 * Amazon image URLs follow a structural invariant: the image-ID portion
 * (e.g. `71-TFqhkuFL`) never contains the two-character sequence `._`. Every
 * sizing directive starts with `._` and ends with `_` right before the file
 * extension. We find the last `._` whose following segment ends in `_` and
 * discard everything from that `._` to the dot, then insert the new directive:
 *
 * ```text
 * images/I/71-TFqhkuFL._AC_UY218_.jpg  →  images/I/71-TFqhkuFL._SL1500_.jpg
 * images/I/71-TFqhkuFL._SL1200_.png    →  images/I/71-TFqhkuFL._SL500_.png
 * images/I/71-TFqhkuFL.jpg             →  images/I/71-TFqhkuFL._SL1500_.jpg
 * ```
 *
 * The fallback path (when the URL does not end with `.jpg` or `.png`, or
 * has no directive to strip) simply appends `._SL{width}_` before the
 * extension, which is correct because the upstream Rust pipeline should
 * have already stripped any existing directive by the time we receive the
 * URL. This function is the defence-in-depth layer for URLs that bypass
 * or predate that normalization.
 */
function applyAmazonSize(url: string, width: number): string {
  // Split out query and fragment before processing the file extension.
  // Otherwise lastIndexOf('.') can match dots in query values, e.g.
  // foo._SL500_.jpg?token=a.b would select .b as the extension.
  const suffixStart = url.search(/[?#]/);
  const resource = suffixStart === -1 ? url : url.slice(0, suffixStart);
  const suffix = suffixStart === -1 ? '' : url.slice(suffixStart);

  const dotIdx = resource.lastIndexOf('.');
  if (dotIdx === -1) return url;

  const ext = resource.slice(dotIdx); // includes the dot, e.g. ".jpg"
  const path = resource.slice(0, dotIdx);

  // Walk backwards through `._` occurrences. The first one whose tail
  // ends in `_` AND contains at least one digit is a sizing directive
  // (e.g. "._SL500_", "._AC_UY218_"). A bare `._SL_` without a number
  // is not a directive — leave it alone.
  let searchEnd = path.length;
  while (searchEnd > 0) {
    const dirStart = path.lastIndexOf('._', searchEnd - 1);
    if (dirStart === -1) break;

    const afterDir = path.slice(dirStart);
    if (afterDir.endsWith('_')) {
      const body = afterDir.slice('._'.length, -1); // "SL500" from "._SL500_"
      if (body.length > 0 && /\d/.test(body)) {
        return `${path.slice(0, dirStart)}._SL${width}_.${ext.slice(1)}${suffix}`;
      }
    }
    searchEnd = dirStart;
  }

  // No existing directive — append the new one.
  return `${path}._SL${width}_.${ext.slice(1)}${suffix}`;
}

function applyWalmartSize(url: string, width: number): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('odnWidth', String(width));
    parsed.searchParams.set('odnHeight', String(width));
    return parsed.toString();
  } catch {
    return url;
  }
}

function applyShopifySize(url: string, width: number): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('width', String(width));
    return parsed.toString();
  } catch {
    return url;
  }
}

export function getSizedImageUrl(image: DisplayImage, width: number): string {
  switch (image.cdn) {
    case DISPLAY_IMAGE_CDN.Amazon:
      return applyAmazonSize(image.url, width);
    case DISPLAY_IMAGE_CDN.Walmart:
      return applyWalmartSize(image.url, width);
    case DISPLAY_IMAGE_CDN.Shopify:
      return applyShopifySize(image.url, width);
    default:
      return image.url;
  }
}

/**
 * Pass-through Next.js image loader for surfaces that already receive a
 * fully-sized CDN URL from getSizedImageUrl(). This preserves the helper's
 * query-param/path rewriting semantics instead of re-encoding them through the
 * default /_next/image optimizer URL.
 */
export const cdnAwareLoader = ({ src }: { src: string }): string => src;
